import { assert } from '@pn/core/utils/assert';
import { GroupedLogger, LogLevel } from '@pn/core/utils/debug/GroupedLogger';
import { isNil } from 'lodash-es';

const LOG_LEVEL = LogLevel.error;
const SHOW_TIMESTAMPS = false;

const groupedLogger = new GroupedLogger(LOG_LEVEL, SHOW_TIMESTAMPS);
const timers = new Map<string, number>();

export const log = (groupId?: string) => ({
  info: (...args: any[]) => {
    if (!isNil(groupId)) {
      groupedLogger.log(groupId, args);
    } else if (LOG_LEVEL <= LogLevel.info) {
      console.log(...args);
    }
  },
  warn: (...args: any[]) => {
    if (!isNil(groupId)) {
      groupedLogger.warn(groupId, args);
    } else if (LOG_LEVEL <= LogLevel.warn) {
      console.warn(...args);
    }
  },
  time: (label: string) => {
    /* This method will override the existing timer by design */
    timers.set(`${groupId}::${label}`, performance.now());
  },
  timeLog: (label: string, step?: string) => {
    const start = timers.get(`${groupId}::${label}`);
    const display = step ? `${label} [${step}]` : label;
    if (start) {
      const duration = performance.now() - start;
      if (!isNil(groupId)) {
        groupedLogger.log(groupId, [`${display}: ${duration.toFixed(2)} ms`]);
      } else if (LOG_LEVEL <= LogLevel.info) {
        console.log(`${display}: ${duration.toFixed(2)} ms`);
      }
    } else {
      if (!isNil(groupId)) {
        groupedLogger.warn(groupId, [`Timer does not exist for ${label}`]);
      } else if (LOG_LEVEL <= LogLevel.warn) {
        console.warn(`Timer does not exist for ${label}`);
      }
    }
  },
  timeEnd: (label: string) => {
    if (!isNil(groupId)) {
      const start = timers.get(`${groupId}::${label}`);
      if (start) {
        timers.delete(`${groupId}::${label}`);
        const end = performance.now();
        const duration = end - start;
        groupedLogger.log(groupId, [`${label}: ${duration.toFixed(2)} ms`]);
      } else {
        groupedLogger.warn(groupId, [`Timer does not exist for ${label}`]);
      }
    } else {
      const start = timers.get(`${groupId}::${label}`);
      if (start) {
        timers.delete(`${groupId}::${label}`);
        const end = performance.now();
        const duration = end - start;
        if (LOG_LEVEL <= LogLevel.info) {
          console.log(`${label}: ${duration.toFixed(2)} ms`);
        }
      } else if (LOG_LEVEL <= LogLevel.warn) {
        console.warn(`Timer does not exist for ${label}`);
      }
    }
  },
  flush: (collapsed = true) => {
    assert(!isNil(groupId), 'Group ID must be provided to flush logs');
    groupedLogger.flush(groupId, collapsed);
  },
});

/**
 * Emulate a delay.
 */
export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
